import { useCallback } from 'react';
import {
  findAllTaskListItems,
  getFindAllTaskListItemsQueryKey,
  useUpdateTask,
} from '@api';
import { useNotification } from '@common';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const TRANS_KEY_NOTIFICATION = 'NOTIFICATIONS.MARK_TASK_READ';
const TASKS_QUERY_KEY = getFindAllTaskListItemsQueryKey();

type TasksListCacheType = Awaited<ReturnType<typeof findAllTaskListItems>>;

type MarkTaskAsReadContextType = {
  previous: TasksListCacheType | undefined;
};

type UseMarkTaskAsReadReturnType = {
  markTaskAsRead: (taskId: string) => void;
};

export const useMarkTaskAsRead = (): UseMarkTaskAsReadReturnType => {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const updateCache = useCallback(
    async (taskId: string) => {
      await queryClient.cancelQueries({ queryKey: TASKS_QUERY_KEY });

      const previous =
        queryClient.getQueryData<TasksListCacheType>(TASKS_QUERY_KEY);

      queryClient.setQueryData<TasksListCacheType>(
        TASKS_QUERY_KEY,
        (current) => {
          if (!current) return current;

          return {
            ...current,
            data: current.data.map((task) =>
              task.id === taskId ? { ...task, isUnread: false } : task
            ),
          };
        }
      );

      return { previous };
    },
    [queryClient]
  );

  const { mutate } = useUpdateTask<AxiosError, MarkTaskAsReadContextType>({
    mutation: {
      onMutate: async ({ taskId }) => updateCache(taskId),
      onError: (error, _variables, context) => {
        queryClient.setQueryData(TASKS_QUERY_KEY, context?.previous);

        showError({
          title: `${TRANS_KEY_NOTIFICATION}.ERROR.TITLE`,
          message: `${TRANS_KEY_NOTIFICATION}.ERROR.MESSAGE`,
          error,
        });
      },
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      },
    },
  });

  const markTaskAsRead = useCallback(
    (taskId: string) => {
      mutate({ taskId, data: { isUnread: false } });
    },
    [mutate]
  );

  return { markTaskAsRead };
};
